<script lang="ts">
    import { ModalManager } from "@/modal-manager";
    import { BUTTON_VARIANT } from "@apps/wishlist/components/WishlistTheme/constants";
    import WishlistTheme from "@apps/wishlist/components/WishlistTheme/WishlistTheme.svelte";
    import { SAVE_WISHLIST_MODAL_ACTION_TYPES } from "@apps/wishlist/constants/constants";
    import { SaveWishlistModalWidgetDefinition } from "@apps/wishlist/definitions";
    import { WishlistProductStatusesQuery } from "@apps/wishlist/queries/WishlistProductStatusesQuery";
    import WishlistGuestInfoService from "@apps/wishlist/services/WishlistGuestInfoService";
    import { WishlistProductStatusesService } from "@apps/wishlist/services/WishlistProductStatusesService";
    import { WishlistsService } from "@apps/wishlist/services/WishlistsService";
    import WishlistsStorefrontValuesService from "@apps/wishlist/services/WishlistsStorefrontValuesService";
    import { saveWishlistModalToken } from "@apps/wishlist/tokens";
    import { CustomerInfoProviderInterface } from "@lib/CustomerInfoProvider/CustomerInfoProviderInterface";
    import { LocalizationInfoProviderInterface } from "@lib/LocalizationInfoProvider/LocalizationInfoProviderInterface";
    import { ProductIdManagerInterface } from "@lib/ProductIdManager/ProductIdManagerInterface";
    import { ProductVariantIdManagerInterface } from "@lib/ProductVariantIdManager/ProductVariantIdManagerInterface";
    import { AuthenticationInfoProviderInterface } from "@modules/authentication/AuthenticationInfoProvider/AuthenticationInfoProviderInterface";
    import { ROLE } from "@modules/authentication/constants";
    import { DynamicImportManager } from "@modules/dynamic_import/DynamicImportManager";
    import CN from "classnames";
    import { combineLatest, lastValueFrom, map, of, switchMap } from "rxjs";
    import { onMount } from "svelte";

    import Badge from "@components/Badge/Badge.svelte";
    import BaseButton from "@components/Buttons/BaseButton.svelte";
    import Button from "@components/Buttons/Button.svelte";
    import T from "@components/T.svelte";
    import WishlistIcon from "@components/WishlistIcon/WishlistIcon.svelte";

    export let productIdManager: ProductIdManagerInterface;
    export let productVariantIdManager: ProductVariantIdManagerInterface;
    export let authenticationInfoProvider: AuthenticationInfoProviderInterface;
    export let wishlistsService: WishlistsService;
    export let buttonClass: string;
    export let modalManager: ModalManager;
    export let wishlistProductStatusesQuery: WishlistProductStatusesQuery;
    export let wishlistProductStatusesService: WishlistProductStatusesService;
    export let dynamicImportManager: DynamicImportManager;
    export let wishlistsStorefrontValuesService: WishlistsStorefrontValuesService;
    export let isShadowRootDisabled: boolean;
    export let wishlistGuestInfoService: WishlistGuestInfoService;
    export let customerInfoProvider: CustomerInfoProviderInterface;
    export let afterWidgetInject: () => void;

    const wishlistBranding$ =
        wishlistsStorefrontValuesService.selectWishlistsBranding();

    let variantId$ = productVariantIdManager.variantId$;
    let productId$ = productIdManager.productId$;

    const settings$ =
        wishlistsStorefrontValuesService.selectWishlistsSettings();

    const companyId = customerInfoProvider.getCompany()?.id;

    const productStatus$ = productId$.pipe(
        switchMap((productId) =>
            productId
                ? wishlistProductStatusesQuery.selectEntity(productId)
                : of()
        )
    );

    const loading$ = productId$.pipe(
        switchMap((productId) =>
            productId
                ? wishlistProductStatusesQuery.selectProductInLoadingStatus(
                      productId
                  )
                : of()
        )
    );

    const isCustomer$ = authenticationInfoProvider
        .selectRole()
        .pipe(map((role) => role === ROLE.Customer));

    const variantInWishlistStatus$ = combineLatest([
        productId$,
        variantId$,
    ]).pipe(
        switchMap(([productId, variantId]) => {
            if (productId && variantId) {
                return wishlistProductStatusesQuery.selectVariantInWishlistStatus(
                    productId,
                    variantId
                );
            }
            return of();
        })
    );

    let handleToggle = () => {
        if ($variantInWishlistStatus$ && $productId$) {
            lastValueFrom(
                wishlistsService.deleteItem({
                    productId: $productId$,
                    variantId: $variantId$ || undefined,
                    companyId,
                })
            );
        } else {
            handleAddItem();
        }
    };

    const handleOpenSaveModal = async () => {
        await dynamicImportManager.fetch({
            definition: SaveWishlistModalWidgetDefinition,
        });
        // Ensure the modal widget initializes asynchronously before opening it.
        setTimeout(() => {
            modalManager.openModal(saveWishlistModalToken, undefined, {
                actionType: SAVE_WISHLIST_MODAL_ACTION_TYPES.addItem,
                productId: $productId$,
                variantId: $variantId$,
            });
        }, 0);
    };

    const handleAddItem = () => {
        const productId = $productId$;
        const variantId = $variantId$;
        if (!$settings$ || !productId || !variantId) return;

        const addItem = () =>
            lastValueFrom(
                wishlistsService.addItem({
                    productId,
                    variantId,
                    wishlistId: null,
                })
            );

        if ($settings$.isDisabledForGuests && !$isCustomer$) {
            const isSaved = wishlistGuestInfoService.isCurrentGuestInfoSaved();
            if (isSaved) {
                addItem();
            } else {
                handleOpenSaveModal();
            }
        } else {
            addItem();
        }
    };

    onMount(() => {
        const productIdSubscription = productId$.subscribe((productId) => {
            if (productId) {
                wishlistProductStatusesService.addProductIdForFetching(
                    productId
                );
            }
        });

        afterWidgetInject();

        return () => {
            productIdSubscription.unsubscribe();
        };
    });

    const DynamicButton =
        buttonClass && isShadowRootDisabled ? BaseButton : Button;
</script>

{#if $wishlistBranding$}
    <WishlistTheme branding={$wishlistBranding$}>
        <div class="gw-wl-add-to-wishlist-product-page-widget">
            <DynamicButton
                type="button"
                data-in-wishlist={!!$variantInWishlistStatus$}
                data-gw-wishlist-items-count={$productStatus$?.wishlistItemsCount ||
                    0}
                on:click={handleToggle}
                variant={BUTTON_VARIANT[
                    $wishlistBranding$.wishlistButtonSettings.style
                ]}
                disabled={$loading$}
                class={CN(buttonClass)}
                withoutReset
                block
            >
                <div class={"gw-wl-add-to-wishlist-btn__inner"}>
                    <WishlistIcon
                        width={25}
                        height={25}
                        isFilled={$variantInWishlistStatus$}
                        iconSettings={$wishlistBranding$.wishlistIconSettings}
                    />

                    {#if $wishlistBranding$.wishlistButtonSettings.isCounterEnabled && $productStatus$?.wishlistItemsCount}
                        <Badge variant="flat" bold transparent>
                            {$productStatus$?.wishlistItemsCount}
                        </Badge>
                    {/if}

                    <span>
                        {#if $variantInWishlistStatus$}
                            <T
                                namespace="wishlist.critical.AddToWishlistProductPageWidget"
                                translationKey="remove_from_wishlist_button_text"
                            />
                        {:else}
                            <T
                                namespace="wishlist.critical.AddToWishlistProductPageWidget"
                                translationKey="add_to_wishlist_button_text"
                            />
                        {/if}
                    </span>
                </div>
            </DynamicButton>
        </div>
    </WishlistTheme>
{/if}

<style lang="postcss">
    .gw-wl-add-to-wishlist-product-page-widget {
        & :global(.gw-btn.gw-button) {
            border-radius: calc(
                1px * var(--gw-wl-wishlistButtonSettings-borderRadius)
            ) !important;
        }

        & :global(.gw-btn.gw-button.--contained) {
            background-color: var(--gw-wl-wishlistButtonSettings-color);
            color: var(--gw-wl-wishlistButtonSettings-fontColor);
            transition: all 0.3s;
            border: 0;
        }

        & :global(.gw-btn.gw-button.--outlined) {
            background-color: transparent;
            box-shadow: 0 0 0 1px var(--gw-wl-wishlistButtonSettings-color);
            color: var(--gw-wl-wishlistButtonSettings-fontColor);
            transition: all 0.3s;
            border: 0;
        }
    }

    .gw-wl-add-to-wishlist-btn__inner {
        display: flex;
        align-items: center;
        gap: 5px;
    }
</style>
